import React, { useRef, useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
//import { imagesURL } from "../../../apiRoutes/apiBase"; // Ajusta la ruta según tu estructura
import MerchantMenuProductsDetails from "../views/website/merchants/menuProductsDetails"; // Ajusta la ruta
import { useTheme } from "@mui/material/styles";

/**
 * Componente ProductsCarousel
 * Renderiza un carrusel horizontal nativo (Swipe) de productos destacados.
 * Cero dependencias externas para maximizar el rendimiento.
 * 
 * @param {Object} props
 * @param {Array} props.items - Arreglo de productos
 * @param {Object} props.merchant - Objeto con la info del comercio
 * @returns {JSX.Element}
 */
export default function ProductsCarousel({ items = [], merchant }) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [product, setProduct] = useState(null);

    // --- MAGIA NIVEL SENIOR: Manejo de estado sin re-renders (Performance) ---
    const sliderRef = useRef(null);
    const dragData = useRef({
        isDown: false,
        startX: 0,
        scrollLeft: 0,
        isDraggingAction: false // Diferencia un click real de un arrastre
    });

    const handleMouseDown = (e) => {
        dragData.current.isDown = true;
        dragData.current.isDraggingAction = false;
        
        // Apagamos el imán (snap) por JS directo para que el arrastre sea ultra fluido
        if (sliderRef.current) sliderRef.current.style.scrollSnapType = 'none';
        
        dragData.current.startX = e.pageX - sliderRef.current.offsetLeft;
        dragData.current.scrollLeft = sliderRef.current.scrollLeft;
    };

    const handleMouseLeave = () => {
        dragData.current.isDown = false;
        if (sliderRef.current) sliderRef.current.style.scrollSnapType = 'x mandatory';
    };

    const handleMouseUp = () => {
        dragData.current.isDown = false;
        if (sliderRef.current) sliderRef.current.style.scrollSnapType = 'x mandatory';
    };

    const handleMouseMove = (e) => {
        if (!dragData.current.isDown) return;
        e.preventDefault(); // Previene selección de texto
        
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - dragData.current.startX) * 1.5; // Multiplicador de velocidad
        
        // Si se mueve más de 10px, lo consideramos un Swipe y no un Click
        if (Math.abs(walk) > 10) {
            dragData.current.isDraggingAction = true;
        }
        
        sliderRef.current.scrollLeft = dragData.current.scrollLeft - walk;
    };

    const handleProductClick = (item, e) => {
        // Si el usuario estaba arrastrando, anulamos la apertura del modal
        if (dragData.current.isDraggingAction) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        
        // Click limpio -> abrimos producto
        setProduct(item);
        setOpen(true);
    };
    // -----------------------------------------------------------

    if (!items || items.length === 0) return null;

    return (
        <Box sx={{ width: '100%' }}>
            <Stack
                direction="row"
                spacing={2.5}
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                sx={{
                    overflowX: 'auto',
                    pb: 2,
                    mx: { xs: -2, md: -3 }, 
                    px: { xs: 2, md: 3 },  
                    scrollPaddingLeft: { xs: '16px', md: '24px' }, 
                    
                    // El navegador maneja el cursor instantáneamente sin usar estado de React
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    
                    scrollSnapType: 'x mandatory', 
                    WebkitOverflowScrolling: 'touch',
                    
                    '&::-webkit-scrollbar': { display: 'none' },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    userSelect: 'none' 
                }}
            >
                {items.map((item) => (
                    <Box
                        key={item.id || item.uuid}
                        onClick={(e) => handleProductClick(item, e)}
                        sx={{
                            minWidth: { xs: 140, sm: 160, md: 180 },
                            maxWidth: { xs: 140, sm: 160, md: 180 },
                            flexShrink: 0, 
                            scrollSnapAlign: 'start',
                            display: 'flex',
                            flexDirection: 'column',
                            group: 'true'
                            // Eliminamos el pointerEvents que generaba bloqueos
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                aspectRatio: '1 / 1',
                                borderRadius: 4,
                                overflow: 'hidden',
                                bgcolor: '#f4f6f8',
                                mb: 1.5,
                                border: '1px solid #eaeaea',
                            }}
                        >
                            <img
                                src={item.image_url}
                                alt={item.name}
                                draggable="false" // Evita el "ghosting" de la imagen al arrastrar
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'transform 0.3s ease-in-out',
                                }}
                                // Solo aplicamos hover si el dispositivo soporta mouse (evita bugs en móvil)
                                onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.08)' }}
                                onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                            />
                        </Box>

                        <Box sx={{ px: 0.5 }}>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight="bold" 
                                sx={{ 
                                    color: '#1a1a1a',
                                    lineHeight: 1.2,
                                    mb: 0.5,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {item.name}
                            </Typography>
                            
                            {item.price_display && (
                                <Typography variant="body2" fontWeight="800" color={theme.palette.primary.main}>
                                    ${item.price_display}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
                
                <Box sx={{ minWidth: { xs: '1px', sm: '10px' }, display: 'block', flexShrink: 0 }} />
                
            </Stack>

            {product && (
                <MerchantMenuProductsDetails
                    open={open}
                    onClose={() => {
                        setOpen(false);
                        setTimeout(() => setProduct(null), 300);
                    }}
                    productData={product}
                    merchant={merchant}
                />
            )}
        </Box>
    );
}